// English translations - base language
// Other languages are translated via Claude API

export type Language = 
  | 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' 
  | 'zh' | 'ja' | 'ko' | 'hi' | 'ar' | 'he' 
  | 'th' | 'vi' | 'id' | 'ms' | 'tl' | 'uk' 
  | 'ru' | 'pl' | 'nl' | 'sv' | 'no' | 'el' 
  | 'tr' | 'da' | 'fi' | 'cs' | 'ro' | 'hu';

export const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  hi: 'हिन्दी',
  ar: 'العربية',
  he: 'עברית',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  tl: 'Tagalog',
  uk: 'Українська',
  ru: 'Русский',
  pl: 'Polski',
  nl: 'Nederlands',
  sv: 'Svenska',
  no: 'Norsk',
  el: 'Ελληνικά',
  tr: 'Türkçe',
  da: 'Dansk',
  fi: 'Suomi',
  cs: 'Čeština',
  ro: 'Română',
  hu: 'Magyar',
};

// Base English translations
// v2 — bust build cache
export const englishTranslations: Record<string, string> = {
  // Greetings
  welcome: 'Welcome',
  _v: '2',
  goodMorning: 'Good Morning',
  goodAfternoon: 'Good Afternoon',
  goodEvening: 'Good Evening',
  friend: 'Friend',
  
  // Navigation
  home: 'Home',
  about: 'About',
  settings: 'Settings',
  themeLight: 'Light',
  themeDark: 'Dark',
  themeAuto: 'Auto',
  emergency: 'Emergency',
  
  // Main features
  talkToMe: 'Talk to Me',
  imHereToListen: "I'm Here to Listen",
  reminders: 'Reminders',
  myMood: 'My Mood',
  exercise: 'Exercise',
  brainGames: 'Brain Games',
  dailyWisdom: 'Daily Wisdom',
  healthResources: 'Health Resources',
  familySharing: 'Family Sharing',
  hobbies: 'My Hobbies',
  
  // Reminder types
  water: 'Drink Water',
  medicine: 'Take Medicine',
  meal: 'Have a Meal',
  washroom: 'Washroom',
  walk: 'Take a Walk',
  custom: 'Custom',
  
  // Reminder settings
  howOften: 'How often?',
  once: 'Once',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quickAddReminder: 'Quick add a reminder:',
  yourReminders: 'Your Reminders',
  loadingReminders: 'Loading your reminders...',
  noReminders: 'No reminders yet',
  tapToAdd: 'Tap the + button to add one',
  addReminder: 'Add Reminder',
  offlineMode: 'Offline Mode — Reminders still work!',
  illRemindYou: "I'll Remind You When It's Time",
  reminderCompleted: 'Reminder Completed!',
  whatNext: 'What would you like to do?',
  rescheduled: 'Rescheduled for next time',
  
  // Mood tracker
  howAreYou: 'How Are You Feeling?',
  wonderful: 'Wonderful',
  good: 'Good',
  okay: 'Okay',
  notGreat: 'Not Great',
  struggling: 'Struggling',
  thankYou: 'Thank You for Sharing',
  buildingGreatDay: "You're Building a Great Day!",
  logAnotherMood: 'Log Another Mood',
  tapTheFace: 'Tap the face that matches your mood',
    wantToShare: 'Want to share how you\'re feeling?',
    emailButton: 'Email',
    textButton: 'Text',
    moodShareSubject: 'My Mood Update from DIPPS',
    moodShareIntro: 'Hey! Just wanted to share - I\'m feeling',
    moodShareToday: 'today!',
    moodShareSentWith: 'Sent with ❤️ from DIPPS',
    moodShareSentFrom: 'Sent from DIPPS',
    encourageWonderful1: "That\'s beautiful! Keep shining! ✨",
    encourageWonderful2: 'Your joy is contagious!',
    encourageWonderful3: 'What a blessing to hear!',
    encourageGood1: 'Glad you\'re doing well!',
    encourageGood2: 'Every good day is a gift!',
    encourageGood3: 'Keep that smile going!',
    encourageOkay1: 'Okay days are okay. You\'re doing great.',
    encourageOkay2: 'Tomorrow is a new day.',
    encourageOkay3: 'Take it one moment at a time.',
    encourageNotGreat1: 'I\'m here with you.',
    encourageNotGreat2: 'Would you like to call someone you love?',
    encourageNotGreat3: 'Remember, you are not alone.',
    encourageStruggling1: 'I\'m so sorry you\'re feeling this way.',
    encourageStruggling2: 'Your feelings are valid.',
    encourageStruggling3: 'Consider reaching out to someone who cares.',
  
  // Chat
  typeMessage: 'Type your message...',
  send: 'Send',
  listen: 'Listen',
  imTired: "I'm Tired",
  tellMeSomethingNice: "Tell Me Something Nice",
  
  // Exercises
  gentleExercises: 'Gentle Exercises',
  chairExercises: 'Chair Exercises',
  bedExercises: 'Bed Exercises',
  kitchenExercises: 'Kitchen Exercises',
  duration: 'Duration',
  benefits: 'Benefits',
  steps: 'Steps',
  markComplete: 'Mark Complete',
  completed: 'Completed',

  // Exercise data - titles
  exerciseTitle1: 'Gentle Arm Circles',
  exerciseTitle2: 'Seated Leg Lifts',
  exerciseTitle3: 'Morning Bed Stretches',
  exerciseTitle4: 'Kitchen Counter Push-ups',
  exerciseTitle5: 'Ankle Rotations',
  // Exercise data - durations
  exerciseDuration1: '2 minutes',
  exerciseDuration2: '3 minutes',
  exerciseDuration3: '5 minutes',
  exerciseDuration4: '2 minutes',
  exerciseDuration5: '2 minutes',
  // Exercise data - benefits
  exerciseBenefits1: 'Improves shoulder flexibility',
  exerciseBenefits2: 'Strengthens thigh muscles',
  exerciseBenefits3: 'Gentle wake-up for muscles',
  exerciseBenefits4: 'Upper body strength',
  exerciseBenefits5: 'Improves circulation',
  // Exercise 1 steps
  exerciseStep1a: 'Sit comfortably in your chair with feet flat on the floor',
  exerciseStep1b: 'Extend your arms out to the sides at shoulder height',
  exerciseStep1c: 'Make small circles forward, then backward',
  exerciseStep1d: 'Rest and repeat 5 times',
  // Exercise 2 steps
  exerciseStep2a: 'Sit with your back against the chair',
  exerciseStep2b: 'Slowly lift one leg straight out in front of you',
  exerciseStep2c: 'Hold for 3 seconds, then lower',
  exerciseStep2d: 'Repeat 10 times on each leg',
  // Exercise 3 steps
  exerciseStep3a: 'Lie flat on your back',
  exerciseStep3b: 'Stretch your arms above your head and point your toes',
  exerciseStep3c: 'Hold for 10 seconds and release',
  exerciseStep3d: 'Bring knees gently to chest and hug them',
  exerciseStep3e: 'Rock side to side slowly',
  // Exercise 4 steps
  exerciseStep4a: 'Stand facing the kitchen counter, arm\'s length away',
  exerciseStep4b: 'Place hands on the counter, shoulder-width apart',
  exerciseStep4c: 'Slowly lower your chest toward the counter',
  exerciseStep4d: 'Push back up. Repeat 10 times',
  // Exercise 5 steps
  exerciseStep5a: 'Sit or lie comfortably',
  exerciseStep5b: 'Lift one foot slightly',
  exerciseStep5c: 'Rotate your ankle in circles, 10 times each direction',
  exerciseStep5d: 'Switch to the other foot',
  
  // Games
  memoryMatch: 'Memory Match',
  wordScramble: 'Word Scramble',
  patternGame: 'Pattern Game',
  mathGame: 'Simple Math',
  triviaGame: 'World Trivia',
  wordSearch: 'Word Search',
  sudoku: 'Mini Sudoku',
  spotDifference: 'Spot Difference',
  score: 'Score',
  moves: 'Moves',
  playAgain: 'Play Again',
  backToGames: 'Back to Games',

  gameWonderful: 'Wonderful! 🎉',
  gameCompletedInMoves: 'Completed in {moves} moves!',
  gameHint: 'Hint',
  gameYourAnswer: 'Your answer',
  gameCorrectPlus10: '✓ Correct! +10 points',
  gameTryAgain: 'Try again!',
  gameCheck: 'Check',
  gameNewWord: 'New Word',
  gameWhatComesNext: 'What comes next?',
  gameCorrect: '✓ Correct!',
  gameSkipToNext: 'Skip to next',
  gameQuestionOf: 'Question {current} of {total}',
  gameWrongAnswer: '✗ Wrong! Answer: {answer}',
  gameQuizComplete: 'Quiz Complete! Final Score: {score}/{total}',
  gameFindTheseWords: 'Find these words',
  gameNewPuzzle: 'New Puzzle',
  gameSudokuInstructions: 'Fill in numbers 1-4 (each row, column, and box must have 1-4)',
  gameCheckSolution: 'Check Solution',
  gameRound: 'Round',
  gameSameOrDifferent: 'Are these two images the same or different?',
  gameSame: 'Same ✓',
  gameDifferent: 'Different ✗',
  gameSudokuCompleted: '🎉 Congratulations! Sudoku completed!',
  gameSudokuKeepTrying: 'Not quite right. Keep trying!',
  gameOopsTheyWere: '✗ Oops! They were {result}',
  gameTheSame: 'the same',
  gameDifferentResult: 'different',
  gameFoundMatch: 'You found a match!',
  gameSpelledRight: 'You spelled it right!',
  gameTryRearranging: 'Try rearranging the letters',
  gameGreatKnowledge: 'Great knowledge!',
  gameAnswerWas: 'The answer was {answer}',
  gameBack: '← Back',  

  // Reminders
  timeTo: 'Time to {title}!',
  readAloud: '🔊 Read Aloud',
  needMoreTime: 'Need more time?',
  fiveMin: '5 min',
  tenMin: '10 min',
  thirtyMin: '30 min',
  dismiss: 'Dismiss',
  enableNotifications: 'Enable Notifications for Alerts',
  voiceAnnouncementsOn: '🔊 Voice Announcements ON',
  voiceAnnouncementsOff: '🔇 Voice Announcements OFF',
  testReminderAlert: '🔔 Test Reminder Alert',
  newReminder: 'New Reminder',
  whatTypeReminder: 'What type of reminder?',
  descriptionOptional: '📝 Description (optional)',
  whenRemindYou: '⏰ When should I remind you?',
  suggested: '💡 Suggested',
  useThis: 'Use this',
  doneCheck: 'Done ✓',
  voiceRemindersOn: 'Voice reminders are now on!',
  snoozeConfirm: 'Okay, I will remind you again in {minutes} minutes.',
  timeForYour: 'Time for your {title}!',
  completedAnnounce: '{title} completed',
  shareCompleted: '✅ I just completed my {title} reminder! - Sent from DIPPS',  // Daily Wisdom
  inspiringQuotes: 'Inspiring Quotes for Your Soul',
  shareQuote: 'Share Quote',
  listenToQuote: 'Listen',
  newQuote: 'New Quote',
  
  // Health Resources
  emergencyContacts: 'Emergency Contacts',
  localResources: 'Local Resources',
  addLocalResource: 'Add Local Resource',
  addResource: 'Add Resource',
  yourCountry: 'Your Country',
  resourceName: 'Resource Name',
  phoneNumber: 'Phone Number',
  callNow: 'Call Now',
  
  // Family Sharing
  connectWithLovedOnes: 'Connect with Loved Ones',
  inviteFamily: 'Invite Family Member',
  familyEmail: 'Family Member Email',
  familyName: 'Family Name',
  sendInvite: 'Send Invite',
  pendingInvites: 'Pending Invites',
  activeConnections: 'Active Connections',
  wellnessReport: 'Wellness Report',
  viewReport: 'View Report',
  shareLinkCopied: 'Share Link Copied!',
  removeConnection: 'Remove',
  notifyMissedCheckin: 'Notify on Missed Check-in',
  notifyMoodChange: 'Notify on Mood Change',
  consentRequired: 'Your consent is required to share data with family.',
  iConsent: 'I Consent to Share My Wellness Data',
  
  // Hobbies
  hobbiesDescription: 'Activities That Bring You Joy',
  myHobbies: 'My Hobbies',
  hobbySuggestions: 'Hobby Ideas',
  addCustomHobby: 'Add Your Own Hobby',
  hobbyName: 'Hobby name...',
  selectCategory: 'Select a Category',
  hobbiesEncouragement: 'Hobbies keep your mind active and your heart happy! 💖 Try something new today.',
  activitiesThatBringJoy: 'Activities That Bring Joy',
  
  // About page
  aboutDIPPS: 'About DIPPS',
  laborOfLove: 'A Labor of Love, Built with Heart',
  landAcknowledgment: 'Land Acknowledgment',
  ourStory: 'Our Story',
  globalFamily: 'A Global Family',
  ourPromise: 'Our Promise to You',
  dignityFirst: 'Dignity First',
  privacyProtected: 'Privacy Protected',
  openTransparent: 'Open & Transparent',
  humorWelcome: 'Humor Welcome',
  keepLightsOn: 'Keep the Lights On',
  // About page translations
  aboutSmartCare: "Smart Care for Real Lives",
  aboutCaringCompanion: "A caring companion built with heart",
  aboutDippsFramework: "The DIPPS Framework",
  aboutDippsFrameworkDesc: "In healthcare, particularly for Personal Support Workers (PSWs), DIPPS is a widely used acronym representing the five fundamental principles of compassionate care: Dignity, Independence, Preferences, Privacy, and Safety. These principles guide every interaction between caregivers and the people they support, ensuring that each person is treated with the respect, autonomy, and consideration they deserve.",
  aboutLandAcknowledgmentText: "We acknowledge and respect the Indigenous Peoples on whose traditional territories this app is developed and used, including the Musqueam, Squamish, Tsleil-Waututh, Nuu-chah-nulth, Kwakwaka\'wakw, and many other First Nations whose wisdom, traditions, and deep connection to the land continue to inspire and guide our work.",
  aboutIndigenousCultures: "Indigenous cultures worldwide have always honored their elders as keepers of knowledge, storytellers, and guides. At DIPPS, we are deeply inspired by this universal wisdom and strive to reflect these values in every feature we build.",
  aboutIndigenousQuote: "We do not inherit the Earth from our ancestors; we borrow it from our children.",
  aboutWhyDippsExists: "Why DIPPS Exists: A Wake-Up Call from the Pandemic",
  aboutPandemicText: "The pandemic shook us all, didn\'t it? It pulled back the curtain on how lonely and tough getting older can be, especially when the usual support systems crumble. For so many seniors, it wasn\'t just about the virus—it was about being cut off from family, friends, and the routines that make life feel meaningful.",
  aboutAgingWorld: "Everywhere you look, our world is getting older fast—by 2030, 1 in 6 people will be 60 or more, reaching 2.1 billion by 2050. That\'s not just a statistic; it\'s our parents, our grandparents, our future selves. Families stretch thin, communities feel the weight, and health systems groan under the pressure. British Columbia? We\'re aging quicker than most, with folks 65–74 up 38% and 75+ up 67% since 2001. Privacy, and Safety. These aren\'t just words; they\'re the heart of compassionate care.",
  aboutDippsLaughter: "DIPPS is here to add a little laughter, a lot of ease, and that human touch we all crave. Let\'s make growing older feel like coming home.",
  aboutMomStory: "She\'s still with us, still teaching us every day what it means to age with grace and grit. This app is dedicated to her, and to every elder who deserves to feel seen, connected, and cared for.",
  aboutWrinkleQuote: "Every wrinkle tells a story. Every grey hair is a badge of honor. And every elder deserves technology that respects their journey.",
  aboutEveryCulture: "Every culture honors its elders differently, but the core truth is the same: aging is not a burden—it\'s a privilege. DIPPS celebrates this truth every day.",
  aboutContactUs: "Contact Us",
  aboutContactText: "Have questions or feedback? We\'d love to hear from you.",

  
  // Weather
  weather: 'Weather',
  yourLocation: 'Your Location',
  refresh: 'Refresh',
  tapToChangeUnit: 'Tap to Change Unit',
  weatherError: 'Unable to Load Weather',
  weatherTipCold: "It's cold! Dress warmly and stay safe.",
  weatherTipHot: "It's hot! Stay hydrated and keep cool.",
  weatherTipRain: "Rain is expected. Don't forget your umbrella!",
  weatherTipSnow: "Snow is expected. Please be careful on icy surfaces.",
  weatherTipNice: "Beautiful weather for a gentle walk!",
  
  // Support
  availableIn30Languages: 'Available in 30+ Languages • Works on All Devices',
  yourCaringCompanion: 'Your Caring Companion',
  youreDoingWonderfully: "You're doing wonderfully today. Let's take care of you.",
  
  // Language settings
  language: 'Language',
  selectLanguage: 'Select Language',
  translating: 'Translating...',
  translationError: 'Translation failed. Using English.',
  poweredByClaude: 'Powered by Claude AI',
  
  // Common actions
  done: 'Done',
  checkIn: 'Check In',
  share: 'Share',

      // Settings page
    personalizeExperience: 'Personalize your experience',
    signOut: 'Sign Out',
    deleteAccount: 'Delete Account',
    deleteMyAccount: 'Delete My Account',
    familyMonitoring: 'Family Monitoring',
    primaryEmergencyContact: 'Primary Emergency Contact',
    familyMemberEmail: 'Family Member Email',
    familyCanViewCheckIns: 'Family can view your check-ins',
    yourPrivacyRespected: 'Your privacy is always respected',
    emergencyNumber: 'Emergency Number',
  shareVia: 'Share via',
  email: 'Email',
  sms: 'Text Message',
  save: 'Save',
    enterPhoneNumber: 'Enter phone number',
  delete: 'Delete',
  add: 'Add',
  added: 'Added',
  loading: 'Loading...',
  error: 'Error',
  retry: 'Retry',
  cancel: 'Cancel',
  confirm: 'Confirm',
  back: 'Back',
  next: 'Next',
    voiceCommands: 'Voice Commands',
    learnHandsfreeControls: 'Learn Hands-Free Controls',
      speaking: 'Speaking...',
        aiSettings: 'AI Settings',
          voiceOn: 'Voice ON',
            voiceOff: 'Voice OFF',
              chatPreferences: 'Chat Preferences',
                aiPersonality: 'AI Personality',
                  personalityFriendly: 'Friendly',
                    personalityProfessional: 'Professional',
                      personalityHumorous: 'Humorous',
                        personalityEmpathetic: 'Empathetic',
                          personalityCalm: 'Calm',
                            personalityEnergetic: 'Energetic',
                              personalityWise: 'Wise',
                                personalityWitty: 'Witty',
                                  voiceSelection: 'Voice Selection',
                                    voicePreviewHint: 'Select a voice to hear a preview',
                                      voicePreviewText: 'Hello, I am your AI companion.',
                                        voiceEnhanced: '✨ Enhanced',
                                          preferencesSaved: 'Preferences Saved!',
                                            savePreferences: 'Save Preferences',
                                              listening: 'Listening...',
                                                chatErrorMessage: "I'm having trouble connecting right now. Please try again in a moment.\n\nSafety note: I'm not a doctor or emergency service. If this might be urgent, call 911/112 (or your local emergency number).",
                                                  mode: 'Mode',
                                                    legalDisclaimerPart1: 'DIPPS is not a medical or emergency service. For emergencies call ',
                                                      legalDisclaimerPart2: 'For personal medical advice, talk to a licensed clinician or pharmacist.',
                                                        chatDisclaimerFooter: 'By chatting you agree this is general info — not medical advice.',
  // Settings page
  textSize: 'Text Size',
  chooseTextSize: "Choose the size that's easiest for you to read",
  displayMode: 'Display Mode',
  chooseDisplayMode: 'Choose how the app looks',
  vibrationFeedback: 'Vibration Feedback',
  feelVibrations: 'Feel gentle vibrations when interacting',
  voiceCommands: 'Voice Commands',
  controlWithVoice: 'Control the app with your voice',
  enableVoiceControl: 'Enable Voice Control',
  speakToNavigate: 'Speak to navigate and interact',
  voiceLanguage: 'Voice Language',
  voiceFeedbackSounds: 'Voice Feedback Sounds',
  audioCuesWhenSpeaking: 'Audio cues when speaking',
  alwaysListening: 'Always Listening',
  keepMicOn: 'Keep microphone on after each command',
  soundVolume: 'Sound Volume',
  viewVoiceTutorial: 'View Voice Commands Tutorial',
  safetyContacts: 'Your safety contacts for emergencies',
  forEmergencyLookup: 'For emergency number lookup',
  canadaHealthResources: 'Canada Health Resources',
  importantNumbers: 'Important numbers to have handy',
  allowLovedOnes: 'Allow loved ones to see your check-ins',
  checkIns: 'check-ins',
  logs: 'logs',
  shareYourFeedback: 'Share Your Feedback',
  helpUsImprove: 'Help us improve DIPPS',
  feedbackPlaceholder: "How can we improve? We'd love to hear from you!",
  sendFeedback: 'Send Feedback',
  thankYouFeedback: 'Thank you for your feedback! 💚',
  account: 'Account',
  actionCannotBeUndone: 'This action cannot be undone',
  verifying: 'Verifying...',
  confirm: 'Confirm',
  enterCodeSentToEmail: 'Enter the code sent to your email',
  typeDeleteToConfirm: 'Type "DELETE" to confirm',
  enterCode: 'Enter code',
  off: 'Off',
  emergencyDesc911: 'Life-threatening emergencies',
  crisisSupport: '24/7 crisis support',
  freeHealthAdvice: 'Free health advice 24/7',
  infoForSeniors: 'Information for seniors',
  poisonEmergencies: 'Poison emergencies',
  // Hobbies.tsx - hobby names
  'Painting': 'Painting',
  'Listening to Music': 'Listening to Music',
  'Reading Books': 'Reading Books',
  'Photography': 'Photography',
  'Gardening': 'Gardening',
  'Cooking': 'Cooking',
  'Puzzles': 'Puzzles',
  'Knitting': 'Knitting',
  'Bird Watching': 'Bird Watching',
  'Journaling': 'Journaling',
  'Walking': 'Walking',
  'Tea with Friends': 'Tea with Friends',
  'Card Games': 'Card Games',
  'Crosswords': 'Crosswords',
  'Flower Arranging': 'Flower Arranging',
  'Watching Movies': 'Watching Movies',

  // Privacy.tsx keys
  privacyTitle: 'Privacy Policy',
  privacyLastUpdated: 'Last updated: January 2026',
  privacySimpleVersion: 'The Simple Version',
  privacyDontCollect: "What We DON'T Collect",
  privacyThirdParty: 'Third-Party Services',
  privacyAnthropicTitle: 'Anthropic Claude AI',
  privacyDIPPSPlatformTitle: 'DIPPS Platform',
  privacyOpenMeteoTitle: 'Open-Meteo (Weather)',
  privacyYourControl: 'Your Data, Your Control',
  privacyDeleteStep1: '• Go to Settings → Delete Account',
  privacyDeleteStep2: "• Clear your browser's local storage",
  privacyDeleteStep3: '• Uninstall the app',
  privacyGDPR: 'GDPR & International Users',
  privacyQuestions: 'Questions?',
  privacyCopyright: '© 2026 DIPPS — Built with ❤️ for Elders Everywhere',

  // Terms.tsx
  termsTitle: 'Terms & Conditions',
  termsLastUpdated: 'Last updated: January 2026',
  termsInPlainEnglish: 'In Plain English',
  termsPlainEnglishBody: "DIPPS is a free companion app made with love. Use it to help with daily reminders and have friendly chats. It's not a replacement for doctors, nurses, or real human care. Be kind, have fun, and look after yourself!",
  termsWhatThisAppIs: "What This App Is (And Isn't)",
  termsItIs: '\u2713 It IS:',
  termsIsReminder: '\u2022 A friendly reminder tool',
  termsIsCompanion: '\u2022 A companion for casual chats',
  termsIsMoodTrack: '\u2022 A way to track your mood',
  termsIsGames: '\u2022 Fun brain games to stay sharp',
  termsIsFreeService: '\u2022 A free service',
  termsItIsNot: '\u2717 It is NOT:',
  termsNotMedical: '\u2022 A medical device or health monitor',
  termsNotSubstitute: '\u2022 A substitute for professional healthcare',
  termsNotEmergency: '\u2022 Emergency services (call 911 for emergencies)',
  termsNotReplacement: '\u2022 A replacement for family or caregivers',
  termsMedicalDisclaimer: 'Important Medical Disclaimer',
  termsMedicalBody: 'DIPPS does not provide medical advice, diagnosis, or treatment. The chat feature uses AI which can make mistakes. Never rely on this app for health decisions. Always consult qualified healthcare professionals for medical concerns.',
  termsFairUseAgreement: 'Fair Use Agreement',
  termsFairUsePersonal: 'Use this app for personal, non-commercial purposes',
  termsFairUseNoHack: "Don't try to hack, reverse-engineer, or break the app",
  termsFairUseRespectful: 'Be respectful in chats (the AI remembers nothing anyway!)',
  termsFairUseUpdates: 'We may update these terms; continued use means acceptance',
  termsBuiltWithLove: 'Built With Love',
  termsPoweredBy: 'Powered by Anthropic Claude AI for chats\u2014data processed anonymously, not stored.',
  termsServiceTerms: 'Each service has its own terms. By using this app, you also agree to their terms.',
  termsLiability: 'Limitation of Liability',
  termsLiabilityBody: 'DIPPS is provided "as is" without warranties. We\'re not liable for any damages from using (or not being able to use) this app. This is a passion project, not a commercial product. Use at your own discretion and always prioritize professional advice for important matters.',
  termsCopyright: '\u00A9 2026 DIPPS \u2014 Smart Care for Real Lives',

  // FamilySharing.tsx
  fsCareCircle: 'Care Circle',
  fsSignInDescription: 'Sign in to set up your Care Circle and share wellness updates with family and friends.',
  fsSignInToGetStarted: 'Sign In to Get Started',
  fsShareUpdates: 'Share wellness updates with people you trust',
  fsPrivacyNotice: "DIPPS never shares real names, addresses, health card numbers, or medical details. Only general wellness summaries (mood, activity, streaks) are shared with your circle. You can revoke access anytime.",
  fsInviteToCareCircle: 'Invite to Care Circle',
  fsEnterEmail: 'Enter email address',
  fsLoading: 'Loading...',
  fsNoMembers: 'No members yet. Invite someone above!',
  fsSendUpdateNow: 'Send Update Now',
  fsSendUpdateDescription: 'Send a quick wellness update using your phone. No cost to DIPPS \u2014 uses your own carrier or email.',
  fsWhatToShare: 'What to Share',
  fsChooseWhatToShare: 'Choose what wellness information to include in updates sent to your Care Circle.',
  fsDailyDigestEmail: 'Daily Digest Email',
  fsDailyDigestDescription: 'When enabled, DIPPS sends a daily email summary to accepted Care Circle members.',
  fsEnableDigest: 'Enable Daily Digest',
  fsSendTimeUTC: 'Send Time (UTC)',
  fsDigestPreview: 'What the Digest Looks Like',
  fsDailyWellnessUpdate: 'Daily Wellness Update',
  fsPreviewNote: 'This is a preview. Actual data is generic \u2014 no medical details are shared.',
  fsShareWithFamily: 'Share DIPPS with Family',
  fsDisclaimer: 'DIPPS is not a medical device or substitute for professional healthcare. If you or someone you know is in danger, call 911 or your local emergency number. Always consult a qualified healthcare professional for medical advice.',
  fsShareMood: 'Mood Check-ins',
  fsShareReminders: 'Reminders',
  fsShareExercise: 'Exercise',
  fsShareWisdom: 'Daily Wisdom',
  fsShareGames: 'Games',
  fsShareHobbies: 'Hobbies',
  fsShareHealthResources: 'Health Resources',
  fsShareChat: 'Chat Activity',
  fsJoinCode: 'Join Code',
  fsJoinCodeExpires: 'Code expired — resend invite',
  fsResendInvite: 'Resend Invite',
  fsPendingInvite: 'Waiting to Join',
  fsActiveMembers: 'Active Members',
  fsAlertSettings: 'Alert Settings',
  fsNotifyMissedReminders: 'Missed reminders',
  fsNotifyMood: 'Mood updates',
  fsNotifyInactivity: 'No activity for 24hrs',
  fsNotifySOS: 'SOS alerts',
  fsMemberRemoved: 'Remove from circle',
  fsSOSButton: 'Send SOS Alert',
  fsSOSConfirm: 'Send emergency alert to all family members?',
  fsSOSsent: 'SOS sent to your care circle',
  fsSOSDescription: 'This will immediately email all active family members',
  fsInviteSent: 'Invite sent successfully',
  fsInviteError: 'Could not send invite. Please try again.',
  fsRemoveConfirm: 'Remove this person from your care circle?',
  fsAlertFrequency: 'Alert frequency',
  fsInstant: 'Instant',
  fsDailyDigest: 'Daily digest',
  fsJoinedOn: 'Joined',
  fsWaitingToJoin: 'Waiting to join',
  fsDisplayName: 'Name (optional)',
  fsRemove: 'Remove',
    fsHaveCode: 'Have an invite code?',
    fsJoinACircle: 'Join a Care Circle',

  // HealthResources.tsx
  hrComingSoon: 'Coming Soon! \uD83C\uDF0D',
  hrCustom: 'Custom',
  hrDescription: 'Description',
  hrCancel: 'Cancel',
  hrPlaceholderCountry: 'e.g., Nigeria, Mexico, etc.',
  hrPlaceholderName: 'e.g., Local Hospital',
  hrPlaceholderPhone: 'e.g., 1-800-123-4567',
  hrPlaceholderDescription: 'e.g., 24/7 emergency care',

  // DailyWisdom.tsx
  dwExploreByRegion: 'Explore by Region',
  dwYourFavorites: 'Your Favorites',

  // VoiceTutorial.tsx
  vtAlsoSay: 'You can also say:',
  vtVoiceCommands: 'Voice Commands',
  vtSubtitle: 'Speak to control the app hands-free',
  vtBrowserNotSupported: 'Browser Not Supported',
  vtBrowserNotSupportedBody: "Your browser doesn't support voice commands. Please try using Chrome, Edge, or Safari for voice control features.",
  vtEnableVoice: 'Enable Voice Commands',
  vtControlDescription: 'Control the app by speaking. Navigate pages, scroll, and more \u2014 all hands-free!',
  vtTurnOn: 'Turn On Voice Commands',
  vtQuickStart: 'Quick Start Guide',
  vtTryCommands: 'Try These Commands',
  vtHelpfulTips: 'Helpful Tips',
  vtTipClear: 'Speak clearly and at a normal pace',
  vtTipBeep: 'Wait for the beep before speaking',
  vtTipSimple: 'Use simple, short commands',
  vtTipHelp: 'Say "Help" anytime to see available commands',
  vtTipStop: 'Say "Stop" to turn off voice listening',
  vtVoiceSettings: 'Voice Settings',
  vtChangePrefs: 'Change language and preferences',

  // AcceptInvite.tsx
    aiJoinCircle: 'Join a Care Circle',
    aiEnterCodeHint: 'Enter the 6-digit code from your invitation email',
    aiInvalidCode: 'Please enter a valid 6-digit code',
    aiCodeNotFound: 'Code not found or expired. Ask your family member to resend the invite.',
    aiOwnCircle: 'You cannot join your own care circle.',
    aiJoinNow: 'Join Care Circle',
    aiAccepting: 'Joining...',
    aiSignInRequired: 'Please sign in first',
    aiSignInBody: 'You need to sign in before joining a care circle.',
    aiSignIn: 'Sign In',
    aiWelcome: 'Welcome to the Care Circle!',
    aiJoined: "You're now connected. You'll receive updates about your loved one.",
    aiGoHome: 'Go to Home',
    aiSomethingWrong: 'Something went wrong. Please try again.',
        aiDisclaimer: 'DIPPS — Smart Care for Real Lives. This is not medical advice.',

    // CareChat.tsx
    ccChatWithFamily: 'Family Chat',
    ccQuickWell: "I'm doing well 😊",
    ccQuickCall: 'Can someone call me? 📞',
    ccQuickGreat: 'Having a great day! 🌟',
    ccQuickThinking: 'Thinking of you ❤️',
    ccQuickHelp: 'Need some help 🙏',
    ccSendMessage: 'Send Message',
    ccYourMessage: 'Your own message',
    ccTypeSomething: 'Type something...',
    ccNoMessages: 'No messages yet. Send your first message!',
    ccQuickSend: 'Quick messages',
    ccSending: 'Sending...',
    ccMessageSent: 'Message sent!',
    ccSendError: 'Could not send message. Please try again.',
    ccPrivacyNote: 'Messages are private and only visible to your care circle.',
    ccNoFamily: 'No family members connected yet',
    ccNoFamilyHint: 'Add family members in the Family Sharing section first',
    ccEveryone: 'Everyone',
    ccFamilyMember: 'Family',
    ccToday: 'Today',
    ccReply: 'Reply',
};


// Type-safe translation keys
export type TranslationKey = keyof typeof englishTranslations;
export const translations: Record<'en', Record<string, string>> = {
  en: englishTranslations,
};
